import api from './api';
import type {
  PaginatedResponse,
  Target,
  TargetCreateRequest,
  TargetFilters,
  TargetMetric,
  TargetPeriodType,
} from '@/types';

/**
 * Board #263 / #338 — premium or policy-count targets over a month, a
 * financial quarter or a financial year, and target-vs-achievement.
 *
 * Achievement is NEVER stored: the server derives it from the policy book
 * (`total_premium` or a policy count, bucketed on issue_date falling back to
 * inception_date, restricted to issued, active and matured policies:
 * cancelled, lapsed and expired never count), so these numbers reconcile with
 * the rest of the platform by construction.
 *
 * The achievement envelope is one row per ENTITY (a broker, or a POSP) with a
 * per-month series inside it, which is what both screens render. The
 * `Achievement` interface in `@/types` describes a single target/period pair
 * and does not model that envelope, so the envelope's shape lives here.
 */

/** One month inside an entity's achievement row. */
export interface AchievementPoint {
  period: string;
  year: number;
  month: number;
  /** Decimal strings, like every other money field in this API. */
  target: string;
  achieved: string;
  /** null — not 0 — when no target was set: "unknown", not "missed". */
  percentage: number | null;
}

export interface AchievementRow {
  entity_type: 'broker' | 'posp' | 'channel';
  entity_id: string;
  name: string;
  code: string;
  target: string;
  achieved: string;
  percentage: number | null;
  series: AchievementPoint[];
}

export interface AchievementRanking {
  entity_id: string;
  name: string;
  percentage: number;
}

export interface AchievementResponse {
  from: string;
  to: string;
  months: string[];
  rows: AchievementRow[];
  summary: { target: string; achieved: string; percentage: number | null };
  top: AchievementRanking[];
  bottom: AchievementRanking[];
}

export interface AchievementQuery {
  from?: string;
  to?: string;
  broker?: string;
  posp?: string;
  /** #338: selects one slice of the matrix. Omitted means month / premium / all. */
  period_type?: TargetPeriodType;
  metric?: TargetMetric;
  insurer?: string;
  insurance_type?: string;
  channel?: string;
}

/** `all_posps`: the SERVER enumerates the broker's active POSPs and writes one
 *  individual row per POSP; the client never sends the membership list. */
export interface BulkOptions {
  fan_out?: 'all_posps';
}

export interface BulkTargetRow {
  broker?: string;
  posp?: string | null;
  channel?: string | null;
  insurer?: string | null;
  insurance_type?: string | null;
  metric?: TargetMetric;
  period_type?: TargetPeriodType;
  /** For a quarter or year: the FIRST month of the period. */
  year: number;
  month: number;
  target_amount: string;
}

/** #343 -- the POSP's own tracker. Rupee incentives only; XP is a separate system. */
export interface IncentiveRuleView {
  id: string;
  kind: 'lump_sum' | 'uplift_pct';
  threshold_pct: string;
  amount: string | null;
  percent: string | null;
  tier_label: string;
  reached: boolean;
}

export interface IncentiveProjection {
  estimate: boolean;
  label: string;
  rules: IncentiveRuleView[];
  lump_sum: { rule_id: string; amount: string; tier_label: string } | null;
  uplift: {
    rule_id: string;
    percent: string;
    tier_label: string;
    basis: string;
    /** null while commission is not yet computable: shown as pending, never zero. */
    amount: string | null;
    pending: boolean;
  } | null;
  total: string;
  total_pending: boolean;
  next_milestone: { rule_id: string; threshold_pct: string; still_needed: string; tier_label: string } | null;
}

export interface MyTarget {
  id: string;
  name: string;
  metric: TargetMetric;
  period_type: TargetPeriodType;
  period_start: string;
  period_end: string;
  days_left: number;
  target_amount: string;
  achieved: string;
  percentage: number | null;
  insurer_name: string | null;
  insurance_type_name: string | null;
  projection: IncentiveProjection;
  trend: AchievementPoint[];
}

export interface MyTargetsResponse {
  period_type: TargetPeriodType;
  period: string;
  posp_code: string;
  targets: MyTarget[];
  note: string;
}

export type IncentiveStatus = 'accrued' | 'approved' | 'paid' | 'reversed';

export interface IncentiveEntryRow {
  id: string;
  kind: 'lump_sum' | 'uplift_pct';
  tier_label: string;
  period_start: string;
  amount: string;
  status: IncentiveStatus;
  reason: string;
  paid_on: string | null;
  paid_ref: string;
  recorded_note: string;
}

export interface MyHistoryRow {
  target: string;
  name: string;
  metric: TargetMetric;
  period_type: TargetPeriodType;
  period_start: string;
  period_end: string;
  target_amount: string;
  achieved: string;
  percentage: number | null;
  incentive_net: string;
  entries: IncentiveEntryRow[];
}

function toQuery(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.append(key, String(value));
  });
  const q = search.toString();
  return q ? `?${q}` : '';
}

export const targetService = {
  /** A year's grid is POSPs x 12 rows, which can exceed one page, so this
   *  follows `next` to the end. A truncated list would render blank cells over
   *  stored targets — and a save from that grid would look like it lost them. */
  async listTargets(filters?: TargetFilters): Promise<Target[]> {
    let url = `/targets/${toQuery({
      scope: filters?.scope,
      broker: filters?.broker,
      posp: filters?.posp,
      year: filters?.year,
      month: filters?.month,
      period_type: filters?.period_type,
      metric: filters?.metric,
      insurer: filters?.insurer,
      insurance_type: filters?.insurance_type,
      channel: filters?.channel,
      page_size: 500,
    })}`;
    const all: Target[] = [];
    for (let guard = 0; guard < 25 && url; guard += 1) {
      const response = await api.get<PaginatedResponse<Target> | Target[]>(url);
      const data = response.data;
      if (Array.isArray(data)) return data;
      all.push(...(data.results ?? []));
      const next = data.next;
      if (!next) break;
      // The API returns an absolute URL; api's baseURL already carries the
      // /api/v1 prefix, so keep only the path-and-query beyond it.
      const idx = next.indexOf('/targets/');
      url = idx >= 0 ? next.slice(idx) : '';
    }
    return all;
  },

  async createTarget(payload: TargetCreateRequest & { broker?: string }): Promise<Target> {
    const response = await api.post<Target>('/targets/', payload);
    return response.data;
  },

  /** Upsert many cells at once. Returns the rows the server READ BACK, so a
   *  successful call cannot mean "nothing was persisted". */
  async bulkUpsert(rows: BulkTargetRow[], options?: BulkOptions): Promise<Target[]> {
    const response = await api.post<{ saved: Target[]; count: number }>(
      '/targets/bulk/',
      options?.fan_out ? { rows, fan_out: options.fan_out } : { rows }
    );
    return response.data.saved ?? [];
  },

  async getMine(params: { period_type: TargetPeriodType; period?: string }): Promise<MyTargetsResponse> {
    const response = await api.get<MyTargetsResponse>(`/targets/mine/${toQuery({ ...params })}`);
    return response.data;
  },

  async getMineHistory(period_type?: TargetPeriodType): Promise<MyHistoryRow[]> {
    const response = await api.get<{ results: MyHistoryRow[] }>(
      `/targets/mine/history/${toQuery({ period_type })}`
    );
    return response.data.results ?? [];
  },

  async getAchievement(query?: AchievementQuery): Promise<AchievementResponse> {
    const response = await api.get<AchievementResponse>(
      `/targets/achievement/${toQuery({ ...query })}`
    );
    return response.data;
  },
};

export default targetService;
